import { Box, Link, Typography } from '@dtx-company/design-system/src'
import { FolderSharp } from '@mui/icons-material'
import { IconButton } from '@dtx-company/design-system/src/components/IconButton/IconButton'
import { LinearProgress } from '@dtx-company/design-system/src/components/LinearProgress/LinearProgress'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { StorageVisualizerComponentProps } from './types'
import { Tooltip } from '@dtx-company/design-system/src/components/Tooltip/Tooltip'
import Add from '@mui/icons-material/Add'
import React from 'react'

export const CreateFolderButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <Tooltip
      title={
        <Typography variant="inherit" align="center">
          Add a folder
        </Typography>
      }
      arrow
      placement="bottom-end"
      enterDelay={500}
    >
      <Box>
        <IconButton
          data-testid="create-folder-header-button"
          variant="text"
          onClick={onClick}
          icon={Add}
          color="secondary"
        />
      </Box>
    </Tooltip>
  )
}

export const FoldersEmptyState: React.FC<{ isDesktop?: boolean }> = ({ isDesktop }) => {
  return (
    <Box
      data-testid="folders-menu-empty-state"
      sx={theme => ({
        display: 'flex',
        color: 'onSurface.secondary',
        margin: theme.spacing(0, 8, 0, 4),
        lineHeight: theme.spacing(6)
      })}
    >
      <FolderSharp />
      <Spacer mr={isDesktop ? '12px' : '18px'} />
      <Typography variant="labelLarge">
        Use folders to make sharing & managing assets easier
      </Typography>
    </Box>
  )
}

export const StorageVisualizerComponent: React.FC<StorageVisualizerComponentProps> = ({
  codeCount = 0,
  pageCount = 0,
  codeLimit = 0,
  pageLimit = 0,
  memberCount = 0,
  seatCount = 0,
  showSeatCount
}) => {
  const codePercentage = codeLimit === 0 ? 0 : (codeCount / codeLimit) * 100
  const pagePercentage = pageLimit === 0 ? 0 : (pageCount / pageLimit) * 100
  const seatPercentage = seatCount === 0 ? 0 : (memberCount / seatCount) * 100
  const codeFractionText = codeLimit === 0 ? 'x / x' : `${codeCount} / ${codeLimit}`
  const pageFractionText = pageLimit === 0 ? 'x / x' : `${pageCount} / ${pageLimit}`
  const seatFractionText = seatCount === 0 ? 'x / x' : `${memberCount} / ${seatCount}`

  const progressBarsConfig = [
    ...(codeLimit !== -1
      ? [{ percentage: codePercentage, fractionText: codeFractionText, label: 'Flowcodes' }]
      : []),
    ...(pageLimit !== -1
      ? [{ percentage: pagePercentage, fractionText: pageFractionText, label: 'Flowpages' }]
      : []),
    ...(showSeatCount && seatCount !== -1
      ? [{ percentage: seatPercentage, fractionText: seatFractionText, label: 'Seats' }]
      : [])
  ]

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: 'onSurface.secondary'
      }}
    >
      {progressBarsConfig.map(item => {
        return (
          <Box
            sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            key={`${item.label}-progress-bar`}
          >
            <Spacer mb="16px" />
            <LinearProgress value={item.percentage} color="inherit" />
            <Spacer mb="8px" />
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Typography variant="bodySmall">{item.label}</Typography>
              <Typography variant="bodySmall">{item.fractionText}</Typography>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export const GetMoreStorageLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <Box sx={theme => ({ paddingRight: theme.spacing(4) })}>
      <Link onClick={onClick}>Get more</Link>
    </Box>
  )
}
